import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  OnDestroy,
  output,
  signal,
} from '@angular/core';

/**
 * Organismo: visor de fotos a pantalla completa.
 * Fondo negro, contador «1 de N» y cierre. El pie es opcional.
 */
@Component({
  selector: 'ui-image-lightbox',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './image-lightbox.component.html',
  styleUrl: './image-lightbox.component.scss',
  host: { class: 'ui-image-lightbox-host' },
})
export class UiImageLightboxComponent implements OnDestroy {
  readonly open = input(false);
  readonly images = input<readonly string[]>([]);
  readonly index = input(0);
  readonly alt = input('');
  /** Texto inferior. Vacío oculta el pie. */
  readonly caption = input('');

  readonly openChange = output<boolean>();
  readonly indexChange = output<number>();

  protected readonly zoom = signal(1);
  protected readonly incomingIndex = signal<number | null>(null);
  protected readonly direction = signal<'next' | 'prev'>('next');
  protected readonly pushing = signal(false);
  private pinchDistance = 0;
  private pinchZoom = 1;
  private pinching = false;
  private swipeStartX = 0;
  private swipeStartY = 0;
  private pushFrame = 0;

  protected readonly frames = computed(() => {
    const images = this.images();
    const currentIndex = this.normalizedIndex();
    const current = images[currentIndex];
    if (!current) return [];
    const incomingIndex = this.incomingIndex();
    const incoming = incomingIndex == null ? null : images[incomingIndex];
    if (!incoming || incomingIndex == null) {
      return [{ src: current, index: currentIndex }];
    }
    const incomingFrame = { src: incoming, index: incomingIndex };
    const currentFrame = { src: current, index: currentIndex };
    return this.direction() === 'prev'
      ? [incomingFrame, currentFrame]
      : [currentFrame, incomingFrame];
  });

  constructor() {
    effect(() => {
      if (!this.open()) {
        this.zoom.set(1);
        this.cancelPush();
      }
    });
  }

  ngOnDestroy(): void {
    cancelAnimationFrame(this.pushFrame);
  }

  protected normalizedIndex(): number {
    const total = this.images().length;
    if (!total) return 0;
    return ((this.index() % total) + total) % total;
  }

  protected close(): void {
    this.cancelPush();
    this.zoom.set(1);
    this.openChange.emit(false);
  }

  protected onPushEnd(event: TransitionEvent): void {
    if (
      event.propertyName !== 'transform' ||
      event.target !== event.currentTarget
    ) {
      return;
    }
    const incoming = this.incomingIndex();
    if (incoming == null || !this.pushing()) return;
    this.pushing.set(false);
    this.incomingIndex.set(null);
    this.indexChange.emit(incoming);
  }

  protected onPinchStart(event: TouchEvent): void {
    if (event.touches.length !== 2) return;
    this.pinching = true;
    this.pinchDistance = this.touchDistance(event);
    this.pinchZoom = this.zoom();
  }

  protected onPinchMove(event: TouchEvent): void {
    if (event.touches.length !== 2 || !this.pinchDistance) return;
    const next = this.pinchZoom * (this.touchDistance(event) / this.pinchDistance);
    this.zoom.set(Math.min(4, Math.max(1, next)));
  }

  protected onPinchEnd(event: TouchEvent): void {
    if (event.touches.length >= 2) return;
    this.pinchDistance = 0;
    this.pinching = false;
  }

  protected onSwipeStart(event: PointerEvent): void {
    this.swipeStartX = event.clientX;
    this.swipeStartY = event.clientY;
  }

  protected onSwipeEnd(event: PointerEvent): void {
    if (this.pinching || this.zoom() > 1 || this.incomingIndex() !== null) return;
    const total = this.images().length;
    if (total < 2) return;
    const deltaX = event.clientX - this.swipeStartX;
    const deltaY = event.clientY - this.swipeStartY;
    if (Math.abs(deltaX) < 40 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
    this.zoom.set(1);
    const next = (this.normalizedIndex() + (deltaX < 0 ? 1 : -1) + total) % total;
    this.pushTo(next, deltaX < 0 ? 'next' : 'prev');
  }

  private pushTo(index: number, direction: 'next' | 'prev'): void {
    cancelAnimationFrame(this.pushFrame);
    this.direction.set(direction);
    this.pushing.set(false);
    this.incomingIndex.set(index);
    this.pushFrame = requestAnimationFrame(() => {
      this.pushFrame = requestAnimationFrame(() => {
        if (this.incomingIndex() == null) return;
        this.pushing.set(true);
      });
    });
  }

  private cancelPush(): void {
    cancelAnimationFrame(this.pushFrame);
    this.pushing.set(false);
    this.incomingIndex.set(null);
  }

  private touchDistance(event: TouchEvent): number {
    const [first, second] = [event.touches[0], event.touches[1]];
    return Math.hypot(first.clientX - second.clientX, first.clientY - second.clientY);
  }
}
